import { StateMessage } from "../components/state-message";
import { TagPill } from "../components/tag-pill";
import { formatEventDayAndTime } from "../lib/date";
import { stripMarkdownLinks } from "@repo/markdown";
import { truncateText } from "../lib/text";
import type { ViewProps } from "./types";

const PINBOARD_DESCRIPTION_MAX_CHARS = 130;

export function PinboardView({
  events,
  isLoading,
  isLoadingMore,
  errorMessage,
  nextUrl,
  onSelect,
  onLoadMore,
}: ViewProps) {
  if (isLoading) {
    return <StateMessage>Loading events…</StateMessage>;
  }
  if (errorMessage) {
    return (
      <StateMessage variant="error">
        Unable to load events: {errorMessage}
      </StateMessage>
    );
  }
  if (events.length === 0) {
    return <StateMessage>No matching upcoming events.</StateMessage>;
  }

  return (
    <>
      <div className="gitg-pinboard-grid" aria-busy={isLoadingMore}>
        {events.map((event) => (
          <button
            key={event.id}
            type="button"
            className="gitg-pinboard-card"
            onClick={() => onSelect(event.id)}
          >
            {event.image ? (
              <img
                className="gitg-pinboard-card-image"
                src={event.image}
                alt=""
                aria-hidden="true"
              />
            ) : (
              <div
                className="gitg-pinboard-card-placeholder"
                aria-hidden="true"
              >
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </div>
            )}
            <div className="gitg-pinboard-card-body">
              <p className="gitg-pinboard-card-date">
                {formatEventDayAndTime(event)}
              </p>
              <h3 className="gitg-pinboard-card-title">{event.name}</h3>
              {event.description ? (
                <p className="gitg-pinboard-card-description">
                  {truncateText(
                    stripMarkdownLinks(event.description),
                    PINBOARD_DESCRIPTION_MAX_CHARS,
                  )}
                </p>
              ) : null}
              {event.tags.length > 0 ? (
                <div className="gitg-pinboard-card-tags">
                  {event.tags.map((tag) => (
                    <TagPill key={tag.id} label={tag.name} />
                  ))}
                </div>
              ) : null}
            </div>
          </button>
        ))}
      </div>

      {nextUrl ? (
        <div className="gitg-load-more">
          <button
            className="gitg-load-more-button"
            disabled={isLoadingMore}
            onClick={onLoadMore}
            type="button"
          >
            {isLoadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </>
  );
}
